import moment from "moment";
import { Button } from "primereact/button";
import React, { forwardRef, useCallback, useEffect, useMemo } from "react";
import { readInlineData, usePaginationFragment } from "react-relay";
import { DataTable } from "@components/data-table";
import { NoAccess } from "@components/no-access";
import { useHasPermissions } from "@hooks/use-has-permissions";
import { useTranslation } from "@i18n/hooks";
import { type licensesTable_AccountFragment$key } from "@relay/licensesTable_AccountFragment.graphql";
import {
	type licensesTable_LicenseFragment$data,
	type licensesTable_LicenseFragment$key,
} from "@relay/licensesTable_LicenseFragment.graphql";
import { type licensesTable_LicensesFragment$key } from "@relay/licensesTable_LicensesFragment.graphql";
import { type licensesTable_Refetch } from "@relay/licensesTable_Refetch.graphql";
import { formatDateTimeHumanReadable } from "@util/date-format.utils";
import { ACCOUNT_FRAGMENT, LICENSE_FRAGMENT, QUERY_FRAGMENT } from "./licenses-table.graphql";
import { IdDisplayField, IdWrapper, timeBasedButtonWrapperClass } from "./licenses-table.styles";
import { type LicensesTableProps } from "./licenses-table.types";
import { CreateLicensesButton } from "../create-licenses-button";
import { DeleteLicenseButton } from "../delete-license-button";
import { TransferLicenseButton } from "../transfer-license-button";

const LicensesTable = forwardRef(
	({ licensesFragmentRef, accountName, showDeleteButton, accountId }: LicensesTableProps) => {
		const { t } = useTranslation("common");
		const {
			data: Admin,
			hasPrevious,
			hasNext,
			loadPrevious,
			refetch,
			loadNext,
		} = usePaginationFragment<licensesTable_Refetch, licensesTable_LicensesFragment$key>(
			QUERY_FRAGMENT,
			licensesFragmentRef,
		);

		useEffect(() => {
			refetch({
				nameOpt: accountName,
			});
		}, [accountName]);

		const accountConnection = Admin.Admin.Auth.SelectAccounts.edges?.map((e) =>
			readInlineData<licensesTable_AccountFragment$key>(ACCOUNT_FRAGMENT, e!.node),
		);
		const selectedAccount = useMemo(
			() => accountConnection?.find((acc) => acc.id === accountId),
			[accountId, accountConnection],
		);
		const licensesByAccount = useMemo(
			() =>
				selectedAccount?.extension
					?.licenses!.edges?.map((e) =>
						readInlineData<licensesTable_LicenseFragment$key>(
							LICENSE_FRAGMENT,
							e!.node,
						),
					)
					//?? this is temporary, we need a sort parameter from backend
					.sort((a, b) => {
						const dateA = new Date(
							a.creationInfo.createdAt.replace("[UTC]", ""),
						).getTime();
						const dateB = new Date(
							b.creationInfo.createdAt.replace("[UTC]", ""),
						).getTime();
						return dateB - dateA;
					}),
			[selectedAccount],
		);

		const licenseConnectionId = selectedAccount?.extension?.licenses!.__id;
		const buildCreationInfoColumnBody = (license: licensesTable_LicenseFragment$data) => {
			switch (license.creationInfo.kind) {
				case "Order":
					return (
						<IdWrapper>
							{t("table.order")}:
							<IdDisplayField id={license.creationInfo.orderId ?? ""} />
						</IdWrapper>
					);
				case "Admin":
					return (
						<IdWrapper>
							{t("table.admin")}:
							<IdDisplayField id={license.creationInfo.creatorId ?? ""} />
						</IdWrapper>
					);
				case "Registration":
					return (
						<IdWrapper>
							{t("table.registration")}:
							<IdDisplayField id={license.creationInfo.userId ?? ""} />
						</IdWrapper>
					);
				default:
					return <div>N/A</div>;
			}
		};
		const buildUsedAtColumnBody = (license: licensesTable_LicenseFragment$data) => {
			return license.usage.kind === "UsedForTree"
				? moment(license.usage.at?.replace("[UTC]", "")).format("DD.MM.YYYY - HH:mm")
				: t("license.notUsed");
		};

		const buildCreatedAtColumnBody = (license: licensesTable_LicenseFragment$data) => {
			return moment(license.creationInfo.createdAt.replace("[UTC]", "")).format(
				"DD.MM.YYYY - HH:mm",
			);
		};

		const buildTimeBasedInfo = (license: licensesTable_LicenseFragment$data) => {
			if (license.timeBasedInfo) {
				return `${formatDateTimeHumanReadable(
					license?.timeBasedInfo?.startDateTime,
				)} - ${formatDateTimeHumanReadable(license?.timeBasedInfo?.endDateTime)}`;
			}
			return "-";
		};

		const buildLicenseDefinitionColumnBody = (license: licensesTable_LicenseFragment$data) => {
			return license.creationInfo?.licenseDefinition?.data.name;
		};

		const buildUsedByColumnBody = (license: licensesTable_LicenseFragment$data) =>
			license.usage.kind === "UsedForTree"
				? license.usage.forUser?.email
				: t("license.notUsed");

		const buildActionColumnBody = (license: licensesTable_LicenseFragment$data) => {
			const canDelete =
				licenseConnectionId && showDeleteButton && license.usage.kind === "Free";
			const canTransfer = licenseConnectionId && license.usage.kind === "Free";

			if (canDelete || canTransfer) {
				return (
					<div className={timeBasedButtonWrapperClass}>
						{canDelete && (
							<DeleteLicenseButton
								selectedIds={[license.id]}
								connectionId={licenseConnectionId}
							/>
						)}
						{canTransfer && (
							<TransferLicenseButton
								selectedIds={[license.id]}
								connectionId={licenseConnectionId}
								currentAccountId={accountId}
								currentAccountName={selectedAccount?.name}
							/>
						)}
					</div>
				);
			}

			return undefined;
		};

		const handlePreviousOnClick = useCallback(() => loadPrevious(20), [loadPrevious]);
		const handleNextOnClick = useCallback(() => loadNext(20), [loadNext]);

		const noAccountSpecified = <div>{t("account.noAccountSelected")}</div>;

		const canRead = useHasPermissions(["UserInAccountPermission_LicenseAdmin_Read"]);

		const table = useMemo(() => {
			if (!licensesByAccount) return noAccountSpecified;
			return (
				<React.Fragment>
					<DataTable
						responsiveLayout="stack"
						emptyMessage={t("table.noLicenses")}
						className="mb-3"
						value={licensesByAccount}
						header={
							<div className="flex flex-row justify-content-between align-items-center">
								<div className="flex flex-row">
									{t("license.licensesFor")} {selectedAccount?.name}
								</div>
								<CreateLicensesButton
									accountId={accountId}
									connectionId={licenseConnectionId}
									onLicenseCreated={() => refetch({ nameOpt: accountName })}
								/>
							</div>
						}
					>
						{(Column) => (
							<React.Fragment>
								<Column
									header={t("table.id")}
									body={(license) => <IdDisplayField id={license.id} />}
								/>
								<Column
									header={t("table.createdBy")}
									body={buildCreationInfoColumnBody}
								/>
								<Column
									header={t("table.licenseDefinition")}
									body={buildLicenseDefinitionColumnBody}
								/>
								<Column
									header={t("license.validityPeriod")}
									body={buildTimeBasedInfo}
								/>
								<Column
									header={t("table.createdAt")}
									body={buildCreatedAtColumnBody}
								/>
								<Column header={t("table.usedAt")} body={buildUsedAtColumnBody} />
								<Column header={t("table.usedBy")} body={buildUsedByColumnBody} />
								<Column header={t("table.actions")} body={buildActionColumnBody} />
							</React.Fragment>
						)}
					</DataTable>
					<div className="flex justify-content-center align-items-center">
						<Button
							disabled={!hasPrevious}
							onClick={handlePreviousOnClick}
							className="mr-3 p-button-secondary"
						>
							{t("actions.back")}
						</Button>
						<Button
							className="p-button-secondary"
							disabled={!hasNext}
							onClick={handleNextOnClick}
						>
							{t("actions.next")}
						</Button>
					</div>
				</React.Fragment>
			);
		}, [licensesByAccount, t]);

		if (!canRead) return <NoAccess />;

		return <>{table}</>;
	},
);

LicensesTable.displayName = "LicensesTable";

export { LicensesTable };
